import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, FileVideo, ListChecks, CalendarDays } from "lucide-react";

const GRADES = ["Grade 7", "Grade 8", "Grade 9", "Grade 10"];
const SUBJECTS = ["Physics", "Chemistry", "Mathematics"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const Pill = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold border transition-all duration-150 ${active ? "bg-emerald-500/15 border-emerald-500/60 text-emerald-300" : "bg-[#111726] border-[#1E293B] text-slate-400 hover:border-[#334155]"}`}>
    {children}
  </button>
);

export default function Calendar({ fixedGrade }) {
  const [grade, setGrade] = useState(fixedGrade || "Grade 7");
  const [subject, setSubject] = useState("All");
  const [events, setEvents] = useState([]);
  const [view, setView] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(fmt(new Date()));

  useEffect(() => { if (fixedGrade) setGrade(fixedGrade); }, [fixedGrade]);

  const load = useCallback(() => {
    const s = subject === "All" ? "" : `&subject=${encodeURIComponent(subject)}`;
    api.get(`/calendar?grade=${encodeURIComponent(grade)}${s}`).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, [grade, subject]);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const m = {};
    events.forEach((e) => { if (!e.date) return; (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const selectedEvents = byDate[selected] || [];
  const move = (delta) => setView(new Date(year, month + delta, 1));
  const todayStr = fmt(new Date());

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="calendar-panel">
      <div className="lg:col-span-8">
        <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-emerald-400" />
              <h2 className="text-xl font-bold font-display">{MONTHS[month]} {year}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev" onClick={() => move(-1)} className="w-9 h-9 rounded-lg bg-[#090D16] border border-[#1E293B] flex items-center justify-center text-slate-300 hover:border-emerald-500/50 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button data-testid="cal-today" onClick={() => { const d = new Date(); setView(new Date(d.getFullYear(), d.getMonth(), 1)); setSelected(fmt(d)); }} className="px-3 h-9 rounded-lg bg-[#090D16] border border-[#1E293B] text-sm text-slate-300 hover:border-emerald-500/50 transition-colors">Today</button>
              <button data-testid="cal-next" onClick={() => move(1)} className="w-9 h-9 rounded-lg bg-[#090D16] border border-[#1E293B] flex items-center justify-center text-slate-300 hover:border-emerald-500/50 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>

          {!fixedGrade && <div className="flex flex-wrap gap-2 mb-3">{GRADES.map((g) => <Pill key={g} active={grade === g} onClick={() => setGrade(g)} testid={`cal-grade-${g}`}>{g}</Pill>)}</div>}
          <div className="flex flex-wrap gap-2 mb-4">
            <Pill active={subject === "All"} onClick={() => setSubject("All")} testid="cal-subject-All">All</Pill>
            {SUBJECTS.map((s) => <Pill key={s} active={subject === s} onClick={() => setSubject(s)} testid={`cal-subject-${s}`}>{s}</Pill>)}
          </div>

          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {WD.map((w) => <div key={w} className="text-center text-xs font-semibold text-slate-500 py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const ds = fmt(d);
              const evs = byDate[ds] || [];
              const isSel = ds === selected;
              const isToday = ds === todayStr;
              const hasLesson = evs.some((e) => e.type === "lesson");
              const hasExam = evs.some((e) => e.type === "exam");
              return (
                <button key={i} data-testid={`cal-day-${ds}`} onClick={() => setSelected(ds)}
                  className={`aspect-square rounded-xl border flex flex-col items-center justify-center gap-1 transition-all duration-150 ${isSel ? "bg-emerald-500/15 border-emerald-500" : evs.length ? "bg-[#090D16] border-[#1E293B] hover:border-emerald-500/40" : "bg-[#090D16] border-[#151b28] hover:border-[#1E293B]"}`}>
                  <span className={`text-sm font-semibold ${isToday ? "text-emerald-400" : isSel ? "text-emerald-200" : "text-slate-300"}`}>{d.getDate()}</span>
                  <div className="flex gap-1 h-1.5">
                    {hasLesson && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                    {hasExam && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Lesson added</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-indigo-400" /> Exam created</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-4">
        <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 sticky top-24">
          <h3 className="font-display font-bold text-lg mb-1">{new Date(selected).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</h3>
          <p className="text-xs text-slate-500 mb-4">{grade}{subject !== "All" ? ` · ${subject}` : ""}</p>
          <div className="space-y-2">
            {selectedEvents.length === 0 && <div className="text-sm text-slate-600 py-6 text-center">Nothing scheduled on this day.</div>}
            {selectedEvents.map((e, i) => (
              <motion.div key={e.id + i} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className="p-3 rounded-xl bg-[#090D16] border border-[#1E293B] flex items-start gap-3" data-testid={`cal-event-${e.id}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${e.type === "lesson" ? "bg-emerald-500/15 text-emerald-400" : "bg-indigo-500/15 text-indigo-400"}`}>
                  {e.type === "lesson" ? <FileVideo className="w-4 h-4" /> : <ListChecks className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-100 truncate">{e.title}</div>
                  <div className="text-xs text-slate-500">{e.type === "lesson" ? "Lesson" : "Exam"} · {e.subject}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
